import { Table, useBreakpoint } from '@koyeb/design-system';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { apiQuery, mapSnapshot, mapVolume, refetchInterval, useService } from 'src/api';
import { useCurrentProjectId } from 'src/api/hooks/project';
import { formatBytes } from 'src/application/memory';
import { Input } from 'src/components/forms/input';
import { LinkButton } from 'src/components/link';
import { NoResource } from 'src/components/no-resource';
import { Pagination, usePagination } from 'src/components/pagination';
import { QueryGuard } from 'src/components/query-error';
import { RegionFlag } from 'src/components/region-flag';
import { RegionName } from 'src/components/region-name';
import { ServiceTypeIcon } from 'src/components/service-type-icon';
import { VolumeStatusBadge } from 'src/components/status-badges';
import { InfoTooltip } from 'src/components/tooltip';
import { IconSearch } from 'src/icons';
import { FormattedDistanceToNow } from 'src/intl/formatted';
import { createTranslate } from 'src/intl/translate';
import { Volume } from 'src/model';

import { AttachVolumeButton } from './attach-volume-button';
import { CreateSnapshotDialog } from './create-snapshot-dialog';
import { EditVolumeDialog } from './edit-volume-dialog';
import { VolumeActions } from './volume-actions';

const T = createTranslate('pages.volumes.list');

export function VolumesListSection() {
  const t = T.useTranslate();

  const [projectId] = useCurrentProjectId();

  const pagination = usePagination();
  const [name, setName] = useState('');

  const query = useQuery({
    ...apiQuery('get /v1/volumes', {
      query: {
        ...pagination.query,
        project_id: projectId,
        name: name || undefined,
      },
    }),
    placeholderData: keepPreviousData,
    select: ({ volumes, has_next }) => ({
      volumes: volumes!.map(mapVolume),
      hasNext: Boolean(has_next),
    }),
  });

  pagination.useSync(query.data);

  return (
    <QueryGuard query={query}>
      {({ volumes }) => (
        <section className="col gap-4">
          <div className="row items-center justify-between gap-4">
            <Input
              placeholder={t('header.search')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              start={
                <span className="row items-center self-stretch pl-1">
                  <IconSearch className="size-4 text-dim" />
                </span>
              }
            />

            {volumes.length > 0 && (
              <LinkButton to="/volumes/new">
                <T id="header.createVolume" />
              </LinkButton>
            )}
          </div>

          {volumes.length === 0 && (
            <NoResource
              title={<T id="noVolumes.title" />}
              description={<T id="noVolumes.description" />}
              cta={
                <LinkButton to="/volumes/new">
                  <T id="noVolumes.cta" />
                </LinkButton>
              }
            />
          )}

          {volumes.length > 0 && <VolumesList volumes={volumes} />}

          {pagination.hasPages && <Pagination pagination={pagination} />}
        </section>
      )}
    </QueryGuard>
  );
}

export function VolumesList({ volumes }: { volumes: Volume[] }) {
  const lg = !useBreakpoint('lg');

  return (
    <>
      <Table
        items={volumes}
        columns={{
          name: {
            header: <T id="name" />,
            render: (volume) => <VolumeName volume={volume} />,
          },
          status: {
            header: <T id="status" />,
            render: (volume) => <VolumeStatusBadge status={volume.status} />,
          },
          region: {
            hidden: lg,
            header: <T id="region" />,
            render: (volume) => (
              <div className="row items-center gap-2">
                <RegionFlag regionId={volume.region} className="size-4" />
                <RegionName regionId={volume.region} />
              </div>
            ),
          },
          size: {
            header: <T id="size" />,
            className: clsx('w-26'),
            render: (volume) => formatBytes(volume.size, { decimal: true }),
          },
          created: {
            hidden: lg,
            header: <T id="created" />,
            className: clsx('w-34'),
            render: (volume) => <FormattedDistanceToNow value={volume.createdAt} />,
          },
          service: {
            hidden: lg,
            header: <T id="attachedTo" />,
            className: clsx('w-26'),
            render: (volume) => <AttachedService volume={volume} serviceId={volume.serviceId} />,
          },
          actions: {
            className: clsx('w-[1%]'),
            render: (volume) => <VolumeActions volume={volume} />,
          },
        }}
      />

      <EditVolumeDialog />
      <CreateSnapshotDialog />
    </>
  );
}

function VolumeName({ volume }: { volume: Volume }) {
  const snapshot = useQuery({
    ...apiQuery('get /v1/snapshots/{id}', { path: { id: volume.snapshotId! } }),
    enabled: volume.snapshotId !== undefined,
    refetchInterval: refetchInterval(),
    select: ({ snapshot }) => mapSnapshot(snapshot!),
  });

  return (
    <div className="row items-center gap-2">
      <div className="max-w-xs truncate">{volume.name}</div>
      {snapshot.isSuccess && (
        <InfoTooltip content={<T id="parentSnapshot" values={{ name: snapshot.data.name }} />} />
      )}
    </div>
  );
}

function AttachedService({ volume, serviceId }: { volume: Volume; serviceId?: string }) {
  // todo: fetch all services with a single query (to avoid the n+1 select problem)
  const service = useService(serviceId);

  if (serviceId === undefined) {
    return <AttachVolumeButton volume={volume} />;
  }

  if (!service) {
    return null;
  }

  return (
    <LinkButton to="/services/$serviceId" params={{ serviceId }} variant="outline" color="gray" size={1}>
      <ServiceTypeIcon type={service.type} size={2} />
      {service.name}
    </LinkButton>
  );
}
